export interface CsvFieldSchema {
   name: string
   type: 'string' | 'number' | 'boolean' | 'date' | 'integer'
   required: boolean
   description: string
}

export const PRICING_ADMIN_CSV_SCHEMAS = {
   pricingFileUpload: {
      sampleData: '1234,5678,P,Amoxicilin,AMXILIN,$123.45,$123.00,2025-annual',
      schema: <CsvFieldSchema[]>[
         { name: 'PracticeSKey', type: 'string', required: true, description: 'Internal practice identifier (SKey)' },
         { name: 'PimsID', type: 'string', required: true, description: 'PIMS site identifier' },
         { name: 'RecordType', type: 'string', required: true, description: 'Record type code (e.g., P for product)' },
         { name: 'ProductName', type: 'string', required: false, description: 'Human readable product name' },
         { name: 'Code', type: 'string', required: false, description: 'Product code / CPT / internal code' },
         { name: 'CurrentPrice ($)', type: 'number', required: false, description: 'Current/list price in dollars' },
         { name: 'Recommended Price ($)', type: 'number', required: false, description: 'Recommended (new) price in dollars' },
         { name: 'DisplayTag', type: 'string', required: false, description: 'Display tag / cycle identifier (e.g., 2025-annual)' },
      ],
   },
   ruleSetsFileUpload: {
      sampleData: '1234,Pricing Update Rules,Rules for validating new vs old prices,Yes',
      schema: <CsvFieldSchema[]>[
         { name: 'PracticeSKey', type: 'string', required: true, description: 'Internal practice identifier (SKey)' },
         { name: 'RuleSetName', type: 'string', required: true, description: 'Name of the rule set' },
         { name: 'Description', type: 'string', required: false, description: 'Human readable description of the rule set' },
         { name: 'Active', type: 'string', required: false, description: 'Is the ruleset active? (Yes/No)' },
      ],
   },
   rulesFileUpload: {
      sampleData: 'Pricing Update Rules,1,ReviewPrice,>,LiveSystemPrice,2,Multiplier,New price cannot be more than 2x old price,Yes',
      schema: <CsvFieldSchema[]>[
         { name: 'RuleSetName', type: 'string', required: true, description: 'Name of the rule set this rule belongs to' },
         { name: 'Priority', type: 'integer', required: true, description: 'Rule priority (lower = higher priority)' },
         { name: 'Field', type: 'string', required: true, description: 'Field to evaluate (e.g., ReviewPrice)' },
         { name: 'Operator', type: 'string', required: true, description: 'Comparison operator (e.g., >, <, ==, >=, <=, !=)' },
         { name: 'CompareToField', type: 'string', required: false, description: 'Field to compare against (optional)' },
         { name: 'CompareToValue', type: 'string', required: false, description: 'Literal value to compare against (optional)' },
         { name: 'ValueType', type: 'number', required: false, description: 'Value type (1 = Absolute, 2 = Percent, 3 = Multiplier)' },
         { name: 'WarningMessage', type: 'string', required: false, description: 'Message shown when rule triggers' },
         { name: 'Active', type: 'boolean', required: false, description: 'Is the rule active? (true/false)' },
      ],
   },
   batchFileUpload: {
      tableName: 'revenue_analytics.default.cycle_upload',
      sampleData: '2075,"Mark Allen",1/31/26,1/31/26,22,33,44,55,66,77,88,10,Urban,1/31/26,1/31/26,1/31/26,1,1/31/26,66',
      schema: <CsvFieldSchema[]>[
         { name: 'PracticeSkey', type: 'string', required: true, description: 'Internal practice identifier (SKey)' },
         { name: 'DisplayTag', type: 'string', required: true, description: 'Display tag / cycle identifier (e.g., 2025-annual)' },
         { name: 'PricingCaptain', type: 'string', required: false, description: 'Name of the pricing captain (may include comma, quoted in CSV)' },
         { name: 'ScheduledFor', type: 'date', required: false, description: 'Date scheduled for PIMS writeback or action' },
         { name: 'AnnualRevenue', type: 'number', required: false, description: 'Practice annual revenue' },
         { name: 'TotalVisits', type: 'integer', required: false, description: 'Total visits' },
         { name: 'TotalExistingClients', type: 'integer', required: false, description: 'Total existing clients' },
         { name: 'TotalNewClients', type: 'integer', required: false, description: 'Total new clients' },
         { name: 'Pets', type: 'integer', required: false, description: 'Number of pets' },
         { name: 'MedianDaysToBook', type: 'number', required: false, description: 'Median days to book an appointment' },
         { name: 'ClientMedianIncome', type: 'number', required: false, description: 'Median income of clients' },
         { name: 'MedianDriveTime', type: 'number', required: false, description: 'Median drive time' },
         { name: 'Urbanicity', type: 'string', required: false, description: 'Urbanicity classification (e.g., Urban, Suburban, Rural)' },
         { name: 'DeadlineToSignUp', type: 'date', required: false, description: 'Deadline date to sign up for the cycle' },
         { name: 'DiscussionAvailable', type: 'date', required: false, description: 'Date discussion becomes available' },
         { name: 'WaveStart', type: 'date', required: false, description: 'Wave start date' },
         { name: 'ProposedWave', type: 'integer', required: false, description: 'Proposed wave number' },
         { name: 'DiscussionComplete', type: 'date', required: false, description: 'Date discussion completed' },
         { name: 'CurrentLTMRevenue', type: 'number', required: false, description: 'Current last twelve months revenue' },
      ],
   },
   pricingUpdateDbx: {
      tableName: 'revenue_analytics.default.pricing_updates',
      schema: [
         { name: 'PimsId', type: 'string', nullable: true },
         { name: 'BatchId', type: 'string', nullable: true },
         { name: 'RecordType', type: 'string', nullable: true },
         { name: 'RecordSKey', type: 'string', nullable: true },
         { name: 'Status', type: 'string', nullable: true },
         { name: 'ProcessedOn', type: 'string', format: 'date-time', nullable: true },
         { name: 'Message', type: 'string', nullable: true },
         { name: 'ReviewPrice', type: 'number', nullable: true },
         { name: 'OpsPrice', type: 'number', nullable: true },
         { name: 'FinalPrice', type: 'number', nullable: true },
         { name: 'AppliedPrice', type: 'number', nullable: true },
         { name: 'CurrentPimsPrice', type: 'number', nullable: true },
         { name: 'BatchSKey', type: 'string', nullable: true },
         { name: 'ReviewComment', type: 'string', nullable: true },
         { name: 'FinalComment', type: 'string', nullable: true },
         { name: 'RequestedRevenueImpact', type: 'number', nullable: true },
         { name: 'FinalRevenueImpact', type: 'number', nullable: true },
         { name: 'PracticeSkey', type: 'integer', nullable: true },
         { name: 'PimsSiteId', type: 'string', nullable: true },
         { name: 'CurrentPrice', type: 'number', nullable: true },
         { name: 'NewPrice', type: 'number', nullable: true },
         { name: 'ProductSKey', type: 'string', nullable: true },
         { name: 'PimsProductId', type: 'string', nullable: true },
         { name: 'ParentProductSKey', type: 'string', nullable: true },
         { name: 'ParentPimsProductId', type: 'string', nullable: true },
         { name: 'QuantityBreak', type: 'number', nullable: true },
         { name: 'Code', type: 'string', nullable: true },
         { name: 'PimsFeeTypeId', type: 'string', nullable: true },
         { name: 'PimsCategory', type: 'string', nullable: true },
         { name: 'VCProductSKey', type: 'string', nullable: true },
         { name: 'VCProductCategorySKey', type: 'string', nullable: true },
         { name: 'ProductTypeSKey', type: 'string', nullable: true },
         { name: 'RecordName', type: 'string', nullable: true },
         { name: 'AnnualServiceRevenue', type: 'number', nullable: true },
         { name: 'PctAnnualRevenue', type: 'number', nullable: true },
         { name: 'PctServicesRevenue', type: 'number', nullable: true },
         { name: 'LocalizationIncrease', type: 'string', nullable: true },
         { name: 'CapacityIncrease', type: 'string', nullable: true },
         { name: 'PriceFloorIncrease', type: 'string', nullable: true },
         { name: 'InflationaryIncrease', type: 'string', nullable: true },
         { name: 'RoundingIncrease', type: 'string', nullable: true },
         { name: 'TotalIncrease', type: 'number', nullable: true },
         { name: 'PriceCap', type: 'string', nullable: true },
         { name: 'ClientCount', type: 'integer', nullable: true },
         { name: 'L12MRevenueContribution', type: 'number', nullable: true },
         { name: 'L12MRevenueContributionPct', type: 'number', nullable: true },
         { name: 'L12MDistinctClient', type: 'integer', nullable: true },
         { name: 'L12MQty', type: 'number', nullable: true },
         { name: 'L12MRepeatCustomer', type: 'integer', nullable: true },
         { name: 'L12MRev', type: 'number', nullable: true },
         { name: 'YoYQtyPct', type: 'number', nullable: true },
         { name: 'YoYCustomerPct', type: 'number', nullable: true },
         { name: 'YoYRevPct', type: 'number', nullable: true },
         { name: 'TotalCustomerCodePct', type: 'number', nullable: true },
         { name: 'WellnessPatientCodePct', type: 'number', nullable: true },
         { name: 'PracticeRevPercentile', type: 'string', nullable: true },
         { name: 'PracticeProfitability', type: 'number', nullable: true },
         { name: 'CurrentPricePercentile', type: 'string', nullable: true },
         { name: 'CurrentCompetitorPrice', type: 'string', nullable: true },
         { name: 'CurrentIndexPriceVal', type: 'string', nullable: true },
         { name: 'LowballPriceFloor', type: 'string', nullable: true },
         { name: 'RevenueImpact', type: 'number', nullable: true },
         { name: 'RevenueImpactPct', type: 'number', nullable: true },
         { name: 'L12MTransactionPct', type: 'number', nullable: true },
         { name: 'IsActive', type: 'boolean', nullable: true },
         { name: 'FeeTypeSKey', type: 'string', nullable: true },
         { name: 'LinkedProductSKey', type: 'string', nullable: true },
         { name: 'AverageUnitRetail', type: 'number', nullable: true },
         { name: 'AvgDiscount', type: 'number', nullable: true },
         { name: 'Elasticity', type: 'number', nullable: true },
         { name: 'ModelPriceReason', type: 'integer', nullable: true },
         { name: 'CanUpdatePrice', type: 'boolean', nullable: true },
         { name: 'ConsiderFeeInImpact', type: 'boolean', nullable: true },
         { name: 'L12MTransactions', type: 'integer', nullable: true },
         { name: 'practicename', type: 'string', nullable: true },
         { name: 'regionalmanagername', type: 'string', nullable: true },
         { name: 'level1', type: 'string', nullable: true },
         { name: 'level2', type: 'string', nullable: true },
         { name: 'Id', type: 'integer', nullable: true },
         { name: 'ConsiderDiscountFlag', type: 'boolean', nullable: true },
         { name: 'final_rn', type: 'integer', nullable: true },
         { name: 'rmname', type: 'string', nullable: true },
      ],
   },
}
